<!-- 
  Usage:
  <button {onclick}>
    <ButtonLabel>Click me<ButtonLabel>
  </button>

  <a {href}>
    <ButtonLabel>Click me<ButtonLabel>
  </a>

  NOTES:
  There's no destructive variant! 
  Principle: for dangerous action use a modal with confirmation and primary/secondary button.
 -->

<script>
  import { unique_classes } from '#lib/util.js';
  import Icon from '#lib/Icon.svelte';
  import Text from '#lib/Text.svelte';
  let {
    /** We usually expect a label to be passed as children. But one could also pass some 
     * custom markup when start_icon + label + end_icon does not suffice */
    /** @type {?string|?SvelteSnippet} Text content to display in the button label. */
    children,
    /** @type {string} Text content to display in the button label. If not provided, the children will be used. */
    text = $bindable(null),
    /** Optional destination when the label itself is the link. Omit inside an existing anchor. */
    href,
    /**
     * Defines the visual style of the button. Combines surface and color variants.
     * @type {string} - Space-separated combination of the following:
     * Surface:
     * - 'elevated': Raised appearance with shadow
     * - 'encased': Filled with a border
     * - 'filled': Solid background color
     * - 'linkish': Underlined text, no background
     * - 'underlined': Text with an underline
     * - 'outlined': Transparent with a border color in the text color
     * - 'subtle-outlined': Transparent with a border color in default stroke color
     * - 'tab': Horizontal tab style
     * - 'tab-selected': Active horizontal tab
     * - 'flush': Transparent background, no inline padding
     * - 'ghostly': Transparent, minimal styling
     * - 'segment': Part of a segmented control
     * - 'segment-selected': Active segment in a control
     * - 'frosted': Translucent surface with blur and subtle sheen  
     * - 'beveled': Text or shape appears pressed into the surface  
     * - 'gradient': Surface has a smooth color gradient  

     * Color: 
     * - 'brand'
     * - 'system-destructive'
     * Text size:
     * - 'text-compact'
     * - 'text-default' (not explicitly set)
     * - 'text-large'
     * @example "brand ghostly text-compact"
     */
    variant = 'brand ghostly',
    /**
     * Defines the typography style for the button text.
     * @type {'text-heading3' | 'text-body' | 'text-preamble' | 'text-caption' | 'text-control'}
     */
    text_style = 'text-control',
    /** @type {?string|?SvelteSnippet} icon displayed before the label */
    start_icon = undefined,
    /** @type {?string|?SvelteSnippet} icon displayed after the label */
    end_icon = undefined,
    /** @type {boolean} */
    hover_effect = true,
    /** @type {'start'|'start-stretch'|'center'|'end'|'end-stretch'|'between'} align the label content */
    align = 'center',
    /**
     * Additional CSS classes to apply to the button label.
     * @type {string}
     * @warning Do not add variant classes here. Use the `variant` prop instead.
     */
    class: css_class,
    style,
    /** @type {boolean} */
    editable = false,
    /** @type {function} callback for when the edit link is clicked */
    on_start_icon_click,
    /** @type {function} callback for when the edit link is clicked */
    on_end_icon_click,
    /** @type {string} name of the field to edit (required for Selection API) */
    field_name,
    /** @type {string} shown in place of empty editable text */
    placeholder
  } = $props();

  let button_class = $derived(unique_classes('button-label', variant, align, hover_effect && 'hover-effect', text_style, editable && 'editable', css_class));
  let has_valid_start_icon = $derived(
    (typeof start_icon === 'string' && start_icon.trim() !== '') || typeof start_icon === 'function'
  );
  let has_valid_end_icon = $derived(
    (typeof end_icon === 'string' && end_icon.trim() !== '') || typeof end_icon === 'function'
  );

</script>

{#if start_icon || end_icon || children || text || editable}
  <svelte:element
    this={href && !editable ? 'a' : 'span'}
    href={href && !editable ? href : undefined}
    style='{style}'
    class={button_class}
  >
  
    <!-- Start Icon -->
    {#if has_valid_start_icon}
      <svelte:element 
        class='start-icon-container' this={(editable || on_start_icon_click) ? 'button' : 'span'}
      {...(on_start_icon_click ? {onclick: on_start_icon_click} : {})}
      >
        {#if typeof start_icon === 'string'} 
          <Icon class='button-icon start' name={start_icon} />
        {:else}
          {@render start_icon()} 
          <!-- NOTE: Icon snippets are temporary until we have a solution for animating icons 
            Then we can also use ButtonLabel for "Switch" with a "Switch Icon", or "Checkbox", etc.
          -->
        {/if}
      </svelte:element>
    {/if}
    <!-- Label -->
    {#if children || editable || text}
      {#if editable && typeof text === 'string'}
        <Text
          class='button-text'
          variant={text_style.split('-')[1]}
          {editable}
          bind:content={text}
          {field_name}
          {placeholder}
        />
      {:else if children || text}
        <span class='button-text {text_style} leading-trim'>
          {#if text}
            {text}
          {:else}
            {@render children()}
          {/if}
        </span>
      {/if}
    {/if}
    <!-- End Icon -->
    {#if has_valid_end_icon || (editable && has_valid_start_icon) || (editable && !has_valid_end_icon && !has_valid_start_icon)}
      <svelte:element 
        class='end-icon-container' this={(editable || on_end_icon_click) ? 'button' : 'span'}
        {...(on_end_icon_click ? {onclick: on_end_icon_click} : {})}
      >
        {#if editable && (has_valid_start_icon || (!has_valid_end_icon && !has_valid_start_icon))}
          <Icon name={end_icon || 'link'} />
        {:else if typeof end_icon === 'string'} 
          <!-- HACK: the end_icon should always render with the default color combination. -->
          <Icon class='button-icon end' name={end_icon} />
        {:else if typeof end_icon === 'function'}
          {@render end_icon()}
        {/if}
      </svelte:element>
    {/if}
  </svelte:element>
{/if}

<style lang="postcss">
  :root {
    --default-button-outer-block-spacing: var(--size-2);
  }

  .button-label {
    --icon-size: 17px;
    user-select: none;
    min-width: var(--input-min-height);
    max-width: 100%;
    max-height: 100%;
    display: inline-flex;
    align-items: center;
    transition: opacity 150ms ease, background-color 150ms ease;
    /* potential fix for unsharp edges */
    background-clip: padding-box;
    @media (min-width: 240px) {
      white-space: nowrap;
    }
    * {
      color: inherit;
    }
    /* Vertical padding for button label */
    > * {
      padding-block: var(--input-block-spacing);
      min-height: var(--input-min-height);
      align-content: center;
    }
    /** Hide the text if there's no content. So gap is not applied */
    &:not(.editable) :global(.button-text):empty {
      display: none;
    }
    /* Fix Safari img height */
    :global(img) {
      min-height: var(--icon-size);
    }

    /* Alignment */
    &.start {
      width: fit-content;
      justify-content: flex-start;
      &:not(:has(.button-text)) {
        justify-content: center;
      }
    }
    &.start-stretch {
      justify-content: flex-start;
      width: 100%;
    }
    &.center {
      justify-content: center;
      width: 100%;
      :global(.button-text) {
        text-align: center;
      }
    }
    &.end {
      justify-content: flex-end;
      width: fit-content;
    }
    &.end-stretch {
      justify-content: flex-end;
      width: 100%;
    }
    &.between {
      justify-content: space-between;
      width: 100%;
      /* in case of both icons, add spacing between the text and the end icon (snippet or icon) */
      &:global(:has(.button-icon.start)) :global(.button-text + *) {
        margin-left: auto;
      }
    }
    &:global(:has(> *:nth-child(2))) {
      /* gap: var(--button-label-gap, var(--size-3)); */
    }
    /* equal width and height for icon buttons. no explicit inline-padding for icon-only buttons */
    &:global(:has(.start-icon-container, .end-icon-container)) {
      min-width: var(--input-min-height);
    }
    
    /* Horizontal padding for buttons with text, except for flush, tab, tab-selected, underlined, linkish */
    &:is(
      .flush, 
      .tab, 
      .tab-selected, 
      .underlined, 
      .linkish
    ):has(.button-text:not(:empty)),
    /* Horizontal padding in editable buttons */
    &:is(
      .flush, 
      .tab, 
      .tab-selected, 
      .underlined, 
      .linkish
    ).editable {
      /* Padding at edges */
      --button-label-padding-inline: 0;
    }
    &:is(.tab, .tab-selected) {
      --button-label-padding-inline: calc(var(--input-min-height) / 4 * 0.8);
    }
    /* Don't add padding to the first child if it's empty (e.g. empty start-icon-container) */
    /* Apply start padding to the first non-empty child, unless it's the only child AND an icon container */
    :global( > *:first-child:not(:empty):is(:not(:last-child), :last-child:not(.start-icon-container):not(.end-icon-container))), 
    /* when editing also apply padding, even if empty */
    :global(&.editable > *:first-child:is(:not(:last-child), :last-child:not(.start-icon-container):not(.end-icon-container))), 
    /* In case of an empty start-icon-container, apply start padding to the second child, unless it's the only effective child AND an icon container */
    /* This handles cases where a snippet might render an empty container first */
    :global(&:has(> *:first-child:empty) > *:nth-child(2):is(:not(:last-child), :last-child:not(.start-icon-container):not(.end-icon-container))) {
      padding-inline-start: var(--button-label-padding-inline-start, var(--button-label-padding-inline));
    }
    /* For buttons with only an image (not an icon) add padding. Next to a text the image's
       container is a first/last child and already gets its padding from the rules around this one. */
    :global(&:has(> :is(.start-icon-container, .end-icon-container):first-child:last-child > img:not(.icon))) {
      padding-inline-start: var(--button-label-padding-inline-start, var(--button-label-padding-inline));
      padding-inline-end: var(--button-label-padding-inline-end, var(--button-label-padding-inline));
    }
    /* Apply end padding to the last child, unless it's the only child AND an icon container */
    :global( > *:last-child:is(:not(:first-child), :first-child:not(.start-icon-container):not(.end-icon-container))) {
      padding-inline-end: var(--button-label-padding-inline-end, var(--button-label-padding-inline));
    }
    
    /* Override: Remove inline padding for icon-only buttons (no visible text) */
    /* When .button-text exists but is :empty, it's display:none but still affects sibling selectors above */
    &:not(:has(.button-text:not(:empty))):not(.editable) {
      > .start-icon-container,
      > .end-icon-container {
        padding-inline: 0 !important;
      }
    }

    /* Gap inside */
    :global(&:has( > :nth-child(3)) > *:first-child:not(:last-child)) {
      padding-inline-end: var(--button-label-gap, var(--size-3));
    }
    :global(&:not(:has(> *:first-child:empty)) > *:last-child:not(:first-child)),
    /* When editing also add gap (even if empty) */
    :global(&.editable > *:last-child:not(:first-child)) {
      padding-inline-start: var(--button-label-gap, var(--size-3));
    }
    
    /* Surface Variants */
    /* Border-Radius for all Buttons except underlined */
    &:not(:is(.underlined, .tab, .tab-selected, .flush, .linkish)) {
      border-radius: var(--input-border-radius);
    }
    &.elevated, &.segment-selected {
      box-shadow: 
        inset 0 0 0.5px var(--button-background-color-adjusted), 
        0 1px 1px var(--button-shadow-color), 
        0 1px 5px var(--button-shadow-color), 
        0 1px 10px var(--button-shadow-color);
      background-color: var(--button-background-color);
    }
    &.segment-selected {
      z-index: 1;
    }
    &.encased {
      background-color: var(--button-background-color);
      box-shadow: inset 0 0 0 var(--border-width, 1px) var(--button-stroke-color);
    }
    &.filled {
      background-color: var(--button-background-color);
    }
    &.flush {
      background-color: transparent;
      transition: color 0.1s ease-in-out;
    }
    &.segment {
      transition: color 0.1s ease-in-out;
      background-color: var(--button-background-color);
    }
    &.ghostly {
      transition: opacity 0.1s ease-in-out, background-color 0.1s ease-in-out;
    }
    &.subtle-outlined {
      box-shadow: inset 0 0 0 var(--border-width, 1px) var(--button-stroke-color);
    }
    &.outlined {
      box-shadow: inset 0 0 0 var(--border-width, 1px) var(--button-text-color);
    }
    &.linkish {
      /* --underline-thickness: 1px;
      --underline-color: transparent;
      background-position: 100% calc(50% + 0.5 * var(--font-size) * var(--line-scale)); */
      text-decoration: underline var(--underline-thickness, 1px) var(--underline-color, var(--button-text-color));
      text-underline-offset: 2px;
    }
    &.underlined {
      --underline-thickness: 1px;
      --underline-color: var(--button-text-color);
      background-position: left bottom var(--size-2);
    }
    &.underlined {
      /*
        The line is the first half of a pattern two buttons wide - line, then gap - repeated
        sideways. Shifting it by two widths shows a line again, so on hover (below) the line
        runs out to the right while the next one comes in from the left. A transition, not a
        keyframe animation: a pointer that only passes over reverses it from wherever it got
        to, where a removed animation would snap the line back in a flash.

        The line reaches 2px past both ends of its half and the box clips the excess: Chromium
        snaps repeated tiles to whole pixels, so after the run the line otherwise came to rest
        up to 1.5px short of where it started.

        The run takes the smooth duration on the way out and the fast one back, set through
        --underline-run-duration because the brand buttons in StoryBlock replace this
        transition list with their own.
      */
      background-image: linear-gradient(to right, var(--underline-color) calc(50% + 2px), transparent 0 calc(100% - 2px), var(--underline-color) 0);
      background-repeat: repeat-x;
      background-size: 200% var(--underline-thickness);
      --underline-run-duration: var(--duration-fast);
      transition: background-size 0.2s ease-in-out, background-position-x var(--underline-run-duration, 0s) var(--ease-glide);
    }
    &.tab, &.tab-selected {
      --underline-thickness: 2px;
      --underline-color: var(--button-text-color);
      background-image: linear-gradient(to top, var(--underline-color) var(--underline-thickness), transparent var(--underline-thickness));
      background-position: bottom center;
      background-repeat: no-repeat;
      background-size: calc(100% - 2 * var(--button-label-padding-inline)) 100%;
      transition: background-size 0.2s ease-in-out;
      :global(.flex-row > :is(a,button):has(.button-text):first-child) > & {
        background-position: bottom left;
        background-size: calc(100% - 1 * var(--button-label-padding-inline)) 100%;
        > *:first-child{
          padding-inline-start: 0;
        }
      }
      :global(.flex-row > :is(a,button):has(.button-text):last-child) > & {
        background-position: bottom right;
        background-size: calc(100% - 1 * var(--button-label-padding-inline)) 100%;
        > *:last-child{
          padding-inline-end: 0;
        }
      }
      :global(.flex-row.justify-between > :is(a,button)) > & {
        /* padding-inline-end: 0; */
        /* padding-inline-start: 0; */
      }
      :global(.flex-row:not(.justify-between) > :is(a,button):not(:first-child):not(:last-child)) > & {
        /* background-size: calc( 100%  - var(--input-inline-spacing)) 100%; */
      }
    }
    &.tab:not(:focus-visible) {
      background-image: none;
    }

    &.frosted {
      --frosted-opacity: 0.8;
      background: linear-gradient(to bottom right, 
        oklch(from var(--color-emphasis-background, var(--color-default-emphasis-background)) l c h / var(--frosted-opacity)) 30%, 
        oklch(from var(--color-emphasis-background, var(--color-default-emphasis-background)) l c h / 0.3));
      backdrop-filter: var(--frosted-blur, blur(8px));
      box-shadow: 
        inset 0 0 0 var(--border-width, 1px) oklch(from var(--color-default-background) l c h / 0.2),
        inset 0 -4px 20px oklch(from var(--color-emphasis-background, var(--color-default-emphasis-background)) l c h / 0.2);
    }
    
    &.beveled {
      --_light-mode-adjust: -0.2;
      --_dark-mode-adjust: 0.5;
      --_lightness-adjust: calc( ((var(--color-emphasis-background-lux-mode, 1) + 1) / 2) * var(--_light-mode-adjust) + ((1 - var(--color-emphasis-background-lux-mode, 1)) / 2) * var(--_dark-mode-adjust) );
      --c: oklch(from var(--button-background-color) calc(l + var(--_lightness-adjust)) c h / 0.5);
      background: linear-gradient(to bottom, oklch(from var(--button-background-color) calc(l + var(--_lightness-adjust) * 0.3) c h), var(--button-background-color));
      position: relative;
      box-shadow: var(--shadow-sm);
      &:before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit; 
        background: linear-gradient(var(--button-background-color), var(--c)); /* The border gradient */
        /* Mask to create the inner border effect */
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor; /* For Safari */
        mask-composite: exclude;
        padding: var(--beveled-width, 4px);
        pointer-events: none;
      }
    }

    &.gradient {
      --_gradient-angle: calc(var(--button-gradient-angle, 160deg) + (var(--color-emphasis-background-lux-mode, 1) - 1) / 2 * 180deg);
      --_gradient-lightness-delta: calc(-0.22 * var(--color-emphasis-background-lux-mode, 1) + 0.15);
      background-image: linear-gradient(
          var(--_gradient-angle), 
          var(--button-background-color), 
          oklch(from var(--button-background-color) calc(l + var(--_gradient-lightness-delta)) calc(c * 1.1) h)
        );
    }

    /* Colors */
    /* we first try to use the color passed by the block (e.g. 'canvas'/'accent_1'/'accent_2'), and if not provided fallback to the canvas color */
    &:is(:not(.underlined, .outlined, .subtle-outlined)) {
      --button-stroke-color: var(--color-stroke, var(--color-default-stroke));
    }
    &:is(:not(.filled,.encased,.elevated)) {
      --button-text-color: var(--color-emphasis-background, var( --color-default-emphasis-background));
      --button-icon-color: var(--color-emphasis-background, var( --color-default-emphasis-background));
      --button-background-color: var(--color-default-background, var(--color-default-background));
      --button-background-color-adjusted: oklch(from var(--button-background-color) calc(l + var(--color-background-lux-mode, var(--color-default-background-lux-mode)) * 0.07) c h);
      --button-shadow-color: oklch(from var(--button-background-color) calc(l - 0.4) c h / 0.2);
    }
    &:is(.filled,.encased,.elevated,.segment-selected,.segment, .beveled, .gradient, .frosted) {
      --button-background-color: var(--color-emphasis-background, var( --color-default-emphasis-background));
      --button-background-color-adjusted: oklch(from var(--button-background-color) calc(l + (var(--color-background-lux-mode, var(--color-default-background-lux-mode)) - 0.5) * 0.1) c h);
      /* 
      This formula dynamically adjusts the text color based on the luminance of the control, body, and fill colors.
      It uses the 'lux-mode' values (-1 for dark, 1 for light) to determine the appropriate mix:

      1. When control and body lux modes are the same (both light or both dark):
         - It uses more of the fill color to ensure contrast.
      2. When control and body lux modes are different:
         - It uses more of the body color for better readability.

      The calculation works as follows:
      - Multiplying the lux modes gives 1 if they're the same, -1 if different.
      - Adding 1 to this result gives 2 if same, 0 if different.
      - Multiplying by 50% or dividing by 2 gives the final percentage:
        100% fill color if same, 0% if different (and vice versa for body color).

      The max() and min() functions ensure the percentages stay within the valid 0-100% range.
      */
      --button-text-color: var(--color-emphasis-foreground, var(--color-default-emphasis-foreground));
      --button-icon-color: var(--button-text-color);
    }
    &:is(.elevated) {
      --button-shadow-color: oklch(from var(--color-emphasis-background, var( --color-default-emphasis-background)) calc(l - 0.4) c h / 0.2);
    }
    &:is(.encased) {
      /* Calculate base lightness for stroke: 0% (black) for light BG, 100% (white) for dark BG */
      --_stroke-base-lightness: calc((1 - var(--color-background-lux-mode, 1)) / 2 * 100%); 
      /* Define stroke color using calculated lightness and fixed alpha */
      --button-stroke-color: oklch(var(--_stroke-base-lightness) 0 0 / var(--stroke-opacity, 0.18));
    }
    
    &.system-destructive {
      --button-text-color: var(--color-destructive);
      &:is(.filled, .encased, .elevated, .beveled, .gradient) {
        --button-text-color: #fff;
        --button-icon-color: #fff;
      }
      &:is(
        .tab-selected
        ) {
        --button-text-color: var(--color-destructive);
        --button-icon-color: var(--color-destructive);
      }
      --button-icon-color: var(--button-text-color);
      --button-background-color: var(--color-destructive);
      --button-background-color-adjusted: oklch(from var(--color-destructive) calc(l + var(--color-default-background-lux-mode) * 0.07) c h);
      --button-stroke-color: var(--color-destructive);
      --button-shadow-color: oklch(from var(--color-destructive) calc(l - 0.4) c h / 0.2);
      /* Opioniated: System Buttons should always have a border-radius */
      /* &:not(:is(.underlined, .tab, .tab-selected)) {
        border-radius: max(var(--input-border-radius), var(--size-2));
      } */
    }
    
    &.flush, 
    &.ghostly,
    &.linkish,
    &.underlined,
    &.outlined,
    &.subtle-outlined,
    &.tab, 
    &.tab-selected {
      &:not(.system-destructive) {
        /* Here we purpously use the canvas "BODY" colors, instead of the "control" colors, as these are more associated with texts than buttons */
        --button-text-color: var(--color-foreground, var(--color-default-foreground));
        --button-icon-color: var(--button-text-color);
        --button-background-color: oklch(from var(--color-default-background) calc(l + (0.5 - var(--color-default-background-lux-mode)) * 0.1) c h);
        --button-background-color-adjusted: oklch(from var(--color-default-background-100) calc(l + (0.5 - var(--color-default-background-lux-mode)) * 0.1) c h);
        --button-stroke-color: var(--color-default-stroke);
        --button-shadow-color: oklch(from var(--color-default-background) calc(l - 0.4) c h / 0.2);
      }
    }
    &.segment {
      &:not(.system-destructive) {
        /* Here we purpously use the canvas "BODY" colors, instead of the "control" colors, as these are more associated with texts than buttons */
        --button-text-color: var(--color-foreground, var(--color-default-foreground));
        --button-icon-color: var(--button-text-color);
        /* The same background step as every other quiet surface (see .bg-2), derived
           from the surrounding color set so a segment inside a dark surface — the
           island's tool popovers — stays readable. --color-default-background-50 is
           computed once at :root and would keep the page's light value there. */
        --button-background-color: oklch(from var(--color-background, var(--color-default-background)) calc(l - var(--color-default-background-lux-mode) * 0.05) c h);
      }
    }
    
    /* Apply color variants */
    color: var(--button-text-color);
    
    :global(.button-text) {
      color: var(--button-text-color);
      width: max-content;
      max-width: 100%;
      --color-basesline-grid: oklch(from var(--button-text-color) l c h / 0.3);
      &.editable {
        min-width: 8ch;
      }
    }


    /* Location Variants */
    &.text-compact {
      --input-min-height: var(--menu-item-min-size);
      /* We need to set button-label-padding-inline again locally, as the input-min-height is changing. 
      The global variable --button-label-padding-inline will not update */
      --button-label-padding-inline: calc(var(--input-min-height) / 2 * 0.8);
      :global(.button-text) {
        --size-modifier: 0.875;
      }
    }
    &.text-large {      
      --input-min-height: var(--menu-item-min-size);
      --button-label-padding-inline: calc(var(--input-min-height) / 2 * 0.8);
      :global(.button-text) {
        --size-modifier: 1.25;
      }
    }


    /* Disabled state */
    :global(:is(a,button).disabled) > &,
    :global(button:disabled) > & {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* Hover state */
    @media (hover: hover) {
      &:is(a):hover:not(:active):not(.disabled).hover-effect:not(.editable),
      :global(:is(a,button):hover:not(:disabled):not(:active):not(.disabled)) > &.hover-effect:not(.editable) {
        &:is(.elevated, .encased, .filled, .gradient, .beveled) {
          /* background-color: var(--button-hover-color, var(--button-background-color-adjusted)); */
          opacity: 0.8;
        }
        &:is(.linkish) {
          --underline-color: var(--button-hover-color, var(--button-text-color));
        }
        &:is(.ghostly, .flush, .tab, .segment) {
          opacity: 0.7;
        }
        &:is(.ghostly) {
          /* background-color: var(--button-hover-color, oklch(from var(--button-text-color) l c h / 0.1));
          outline: 4px solid var(--color-default-background, var(--color-default-background));
          outline-offset: -4px; */
        }
        &:is(.outlined, .subtle-outlined) {
          background-color: var(--button-hover-color, oklch(from var(--button-stroke-color) l c h / 0.2));
        }
        &:is(.underlined) {
          /* With motion the line runs instead (below); without it - reduced motion here, the
             site's animations switched off in the last rule below - the line and text lighten */
          @media (prefers-reduced-motion: reduce) {
            opacity: 0.7;
            --underline-color: var(--button-hover-color, oklch(from var(--button-stroke-color) l c h / 0.5));
          }
        }
        &:is(.frosted) {
           --frosted-opacity: 0.8;
        }
      }

      /* Underlined buttons: the line runs through - it leaves to the right and the next one
         comes in from the left, so the link answers the pointer and stays a link. Kept out of
         the rule above because that one ends while the button is pressed: pressing would send
         the line back and releasing a mail or phone link (which does not navigate away) would
         run it a second time. */
      &.underlined.hover-effect:not(.editable):is(a):hover:not(.disabled),
      :global(:is(a,button):hover:not(:disabled):not(.disabled)) > &.underlined.hover-effect:not(.editable) {
        @media (prefers-reduced-motion: no-preference) {
          --underline-run-duration: var(--duration-smooth);
          background-position-x: -200%;
        }
      }
      /* Site animations switched off: no run, the quieter answer instead. Only this opt-out
         depends on an ancestor - popovers are moved to <body>, outside .layout-container, and
         their underlined options must keep answering the pointer */
      :global(.animations-off) &.underlined.hover-effect:not(.editable):is(a):hover:not(:active):not(.disabled),
      :global(.animations-off :is(a,button):hover:not(:disabled):not(:active):not(.disabled)) > &.underlined.hover-effect:not(.editable) {
        background-position-x: 0%;
        opacity: 0.7;
        --underline-color: var(--button-hover-color, oklch(from var(--button-stroke-color) l c h / 0.5));
      }
    }

    /* Active (pressed) state */
    &:is(a):active:not(.disabled):not(.editable),
    :global(:is(a,button):active:not(:disabled):not(.disabled)) > &:not(.editable) {
      &:is(.elevated, .encased, .filled, .gradient, .beveled) {
        background-color: var(--button-active-color, oklch(from var(--button-background-color-adjusted) l c h / 0.8));
      }
      &:is(.ghostly, .flush) {
        --color: oklch(from var(--button-text-color) calc(l + (var(--color-background-lux-mode, var(--color-default-background-lux-mode)) - 0.5) * 0.5) c h);
        color: var(--color);
        --button-icon-color: var(--color);
      }
      &:is(.linkish) {
        --underline-color: var(--button-active-color, var(--button-text-color));
      }
      &:is(.outlined, .subtle-outlined) {
        background-color: var(--button-active-color, oklch(from var(--button-stroke-color) l c h / 0.4));
      }
      &:is(.underlined) {
        --underline-thickness: 2px;
      }
      &:is(.frosted) {
        --frosted-opacity: 0.95;
      }
    }


    /* Focus-visible state */
    :global(:is(a,button):focus-visible) > &:not(.editable) {
      &:is(.elevated, .encased, .filled, .outlined, .subtle-outlined, .ghostly, .flush, .segment, .frosted, .beveled, .gradient) {
        outline: 2px solid var(--button-focus-color, var(--button-stroke-color));
        outline-offset: 2px;
      }
      &:is(.linkish) {
        --underline-thickness: 2px;
        --underline-color: var(--button-focus-color, var(--button-stroke-color));
      }
      &:is(.underlined) {
        --underline-thickness: 2px;
        --underline-color: var(--button-focus-color, var(--button-stroke-color));
      }
    }

    /* Edit mode */
    &.editable {
      cursor: text;
      :global(.button-text) {
        background-clip: content-box; /* clip the editing guidelines */
      }
    }

    /* Icons */
    .start-icon-container, .end-icon-container {
      --icon-color1: var(--button-icon-color1, var(--button-icon-color)); 
      --icon-color2: var(--button-icon-color2, var(--button-icon-color));
      display: flex;
      align-items: center;
      justify-content: center;
      &:empty {
        display: none;
      }
      &button {
        &:hover {
          opacity: 0.7;
        }
      }
    }
  }

  /*************** Global button related helpers ****************************** */
  
  :global(:is(a, button, span):has(> .button-label)){
    /* resets */
    touch-action: manipulation; /* prevents double-tap zoom on touch devices. force browser to immediately respond to taps instead of waiting 300ms for a "potential" 2nd tap */
    text-decoration: none;
    background: transparent;
    border: none;
    /* layout */
    position: relative;
    display: inline-flex;
    align-items: center;
    padding-block: var(--button-outer-block-spacing, var(--button-outer-spacing));
    padding-inline-start: var(--button-outer-inline-spacing-start, var(--button-outer-inline-spacing, var(--button-outer-spacing)));
    padding-inline-end: var(--button-outer-inline-spacing-end, var(--button-outer-inline-spacing, var(--button-outer-spacing)));
    &:focus, &:hover, &:focus-visible {
      outline: none;
    }
  }

  /* Prevent pointer events on the parent button element when the button label is editable. 
  But allow pointer events on the child elements that are interactive. 
  This only works for button but not for <a> elements.
  */
  :global(:is(a, button, span):has(> .button-label.editable)){
    pointer-events: none !important;
    :global(:is(button, [contenteditable="true"], [contenteditable="plaintext-only"], input, label)){
      pointer-events: auto !important;
    }
  }

  /* Multiple buttons in a row with visible surface/ outline need a horizontal gap */
  :where(:global(:is(.flex-row, .flex-wrap):not(.button-group):has(:is(button,a):nth-child(2)):not(.gap-x) > :is(a, button, span):has(> .button-label:not(.ghostly)))) {
    --button-outer-inline-spacing: var(--size-2);
  }


  /* Button Group  */
  :global(.button-group:not(.gap-x):not(.gap-y)) {
    /* Kept as its own variable so the segments inside can derive their corners from the group
       instead of being forced square — see .segment-selected below. */
    --group-border-radius: var(--border-radius, var(--input-border-radius));
    border-radius: var(--group-border-radius);
    width: fit-content;
    /* The site's stroke token, so the group's outline follows the global design settings
       rather than a locally computed grey. A real border (not an outset box-shadow):
       the stroke sits INSIDE the box exactly like every input's border, so grouped
       controls align pixel-identical with inputs at the same outer height. */
    --button-stroke-color: var(--color-default-stroke);
    border: 1px solid var(--button-stroke-color);
    :where(&) {
      overflow: hidden;
    }
    &:not(:has(.segment-selected, .segment)) {
      gap: 1px;
    }
    :global(.button-label) {
      /* background-color: var(--color-default-background); */
    }
    :global(button), :global(a){
      flex-grow: 1;
    }
    :global(.button-label) {
      --input-border-radius: 0;
      --border-width: 0;
    }
    /* The selected segment is the control's "thumb": a square block sitting inside a rounded
       group reads as a mistake, so it carries the group's corner shape. The group clips, so
       the first and last segment still line up with the outer edge. */
    :global(.button-label.segment-selected) {
      --input-border-radius: var(--group-border-radius);
    }
    /* A segmented control is one surface: the group carries the segments' background and
       the segments are transparent on it. Otherwise the rounded thumb sits in a square slot
       and the group's own background shows in its corners. */
    &:has(:is(.segment-selected, .segment)) {
      background-color: oklch(from var(--color-background, var(--color-default-background)) calc(l - var(--color-default-background-lux-mode) * 0.05) c h);
    }
    :global(.button-label.segment) {
      background-color: transparent;
    }
    &:has(:is(.segment-selected, .segment)) {
      transition: background-color 0.15s ease-in-out;
      @media (hover: hover) {
        &:hover {
          background-color: var(--color-default-background-25);
        }
      }
    }
    :global( > :is(button, a):has(.segment, .segment-selected) .button-label) {
      /* Reduce padding between segments */
      --button-label-padding-inline: calc(var(--input-min-height) / 4 * 0.8);
    }
  }

  /* TODO: handle padding for vertical button groups */

  /* Allow text selection for email and phone links globally */
  :global(a[href^="mailto:"]), :global(a[href^="tel:"]) {
    > .button-label {
      user-select: text;
      cursor: text;
    }
    .button-text {
      user-select: text;
    }
  }
</style>
